'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useSubscriptionQuery } from '../api/billingApi';

/**
 * Slim, persistent banner that shows up across the trader app while
 * the user is on a 15-day free trial — and flips into a paywall
 * prompt once the trial has lapsed.
 *
 * Rules:
 *   - Hidden on /billing (the page itself is the next step — no banner).
 *   - Dismissable within a UTC day. Comes back the next day so a
 *     dismissed user doesn't sail past the trial end without seeing it.
 *   - "expired" tone re-shows even after dismiss, because the user is
 *     now in a degraded state and needs to know.
 */
const DISMISSAL_KEY = 'pm.trialBannerDismissedDate';

export function TrialBanner() {
  const router = useRouter();
  const pathname = usePathname();
  const { data: sub } = useSubscriptionQuery();
  const [dismissed, setDismissed] = useState(false);

  // Read dismissal state from localStorage on mount; bucket by UTC date
  // so dismissals naturally expire at midnight.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(DISMISSAL_KEY);
      if (stored === new Date().toISOString().slice(0, 10)) {
        setDismissed(true);
      }
    } catch {
      // localStorage unavailable (Safari private mode etc.) — ignore.
    }
  }, []);

  function dismiss() {
    try {
      window.localStorage.setItem(
        DISMISSAL_KEY,
        new Date().toISOString().slice(0, 10),
      );
    } catch {
      // ignore — the banner just stays visible this session
    }
    setDismissed(true);
  }

  // The banner is the call to action on every other page. On /billing
  // itself the user is already at the destination.
  if (pathname?.startsWith('/billing')) return null;
  if (!sub) return null;

  // Trial expired AND no paid subscription replaced it — ALWAYS show
  // (dismissals don't apply, the user is in a degraded state).
  if (
    sub.status === 'trial_expired' ||
    (sub.status === 'canceled' && sub.tier === 'free')
  ) {
    return (
      <Banner
        tone="expired"
        label="Trial ended"
        message="Your 15-day Pro trial has ended. Subscribe to keep enforcement and monitoring active."
        ctaLabel="Choose a plan"
        onCta={() => router.push('/billing')}
        // Not dismissable — the user needs this every page-load until resolved.
      />
    );
  }

  if (sub.status !== 'trialing' || !sub.trial_end) return null;
  if (dismissed) return null;

  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(sub.trial_end).getTime() - Date.now()) / 86_400_000),
  );

  // The banner gets more urgent as the deadline approaches.
  const urgent = daysLeft <= 3;

  return (
    <Banner
      tone={urgent ? 'urgent' : 'trial'}
      label={`${daysLeft}d left`}
      message={
        daysLeft === 0
          ? 'Your free trial ends today. Subscribe to keep Pro features after midnight.'
          : daysLeft === 1
            ? 'Last day of your free trial — subscribe to keep Pro features.'
            : `You're on the free Pro trial. ${daysLeft} days remaining.`
      }
      ctaLabel={urgent ? 'Subscribe now' : 'See plans'}
      onCta={() => router.push('/billing')}
      onDismiss={dismiss}
    />
  );
}

interface BannerProps {
  tone: 'trial' | 'urgent' | 'expired';
  label: string;
  message: string;
  ctaLabel: string;
  onCta: () => void;
  /** When provided, renders a small × that lets the user hide this banner
   *  for the rest of the UTC day. Intentionally OMITTED for the expired
   *  state — the user can't dismiss a paywall. */
  onDismiss?: () => void;
}

function Banner({ tone, label, message, ctaLabel, onCta, onDismiss }: BannerProps) {
  const palette = TONE_PALETTE[tone];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '10px 18px',
        background: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
        color: palette.fg,
        fontSize: 13,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '3px 9px',
          borderRadius: 999,
          background: palette.chipBg,
          color: palette.chipFg,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, color: palette.fg }}>{message}</span>
      <button
        type="button"
        onClick={onCta}
        style={{
          padding: '6px 14px',
          borderRadius: 8,
          background: palette.btnBg,
          color: palette.btnFg,
          border: `1px solid ${palette.btnBorder}`,
          fontSize: 12.5,
          fontWeight: 500,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {ctaLabel}
      </button>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Hide for today"
          title="Hide for today"
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: 'transparent',
            border: 'none',
            color: palette.fg,
            opacity: 0.55,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
               strokeLinejoin="round">
            <path d="M6 6l12 12" />
            <path d="M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}

const TONE_PALETTE: Record<BannerProps['tone'], {
  bg: string; fg: string; border: string;
  chipBg: string; chipFg: string;
  btnBg: string; btnFg: string; btnBorder: string;
}> = {
  trial: {
    bg: 'var(--pm-cream-2)',
    fg: 'var(--pm-ink-2)',
    border: 'var(--pm-rule)',
    chipBg: 'var(--pm-ink-fixed)',
    chipFg: '#fff',
    btnBg: 'var(--pm-ink-fixed)',
    btnFg: '#fff',
    btnBorder: 'var(--pm-ink-fixed)',
  },
  urgent: {
    bg: 'var(--pm-amber-soft)',
    fg: 'var(--pm-ink)',
    border: 'rgba(199,123,10,0.30)',
    chipBg: 'var(--pm-amber)',
    chipFg: '#fff',
    btnBg: 'var(--pm-ink-fixed)',
    btnFg: '#fff',
    btnBorder: 'var(--pm-ink-fixed)',
  },
  expired: {
    bg: 'var(--pm-coral-soft)',
    fg: 'var(--pm-coral-deep)',
    border: 'rgba(224,65,19,0.30)',
    chipBg: 'var(--pm-coral-deep)',
    chipFg: '#fff',
    btnBg: 'var(--pm-coral-deep)',
    btnFg: '#fff',
    btnBorder: 'var(--pm-coral-deep)',
  },
};
